import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Button, Select, Spin, Table, Typography } from "antd";
import { API_BASE } from "./config/api";
import { getClientToken, handleClientAuthFailure } from "./config/auth";

const authConfig = () => ({ headers: { "X-KG-Client-Authorization": `Bearer ${getClientToken()}` } });
const money = (value) => Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const date = (value) => {
  if (!value) return "-";
  const [y, m, d] = String(value).split("-");
  return y && m && d ? `${d}-${m}-${y}` : value;
};
const monthlyActiveValue = (r) => {
  if (String(r?.sip_status || "").toUpperCase() !== "RUNNING") return 0;
  const amount = Number(r?.sip_amount || 0);
  const frequency = String(r?.frequency || "").toUpperCase();
  if (frequency === "DAILY") return amount * 22;
  if (frequency === "WEEKLY") return amount * 4;
  if (frequency === "MONTHLY") return amount;
  return 0;
};

export default function ClientSips() {
  const [contextLoading, setContextLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isFamily, setIsFamily] = useState(false);
  const [members, setMembers] = useState([]);
  const [selectedPan, setSelectedPan] = useState("ALL");
  const [sipType, setSipType] = useState("active");
  const [rows, setRows] = useState([]);
  const [activeRows, setActiveRows] = useState([]);
  const [zoom, setZoom] = useState(100);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/get_client_dashboard_summary.php`, authConfig());
        if (r.data?.status !== "ok") throw new Error(r.data?.message || "Unable to load client context.");
        const family = Boolean(r.data?.is_family);
        const list = Array.isArray(r.data?.members) ? r.data.members : [];
        setIsFamily(family);
        setMembers(list);
        setSelectedPan(family ? "ALL" : (r.data?.portfolio?.dashboard_pan || "ALL"));
      } catch (e) {
        if (handleClientAuthFailure(e.response, e.response?.data)) return;
        setError(e.response?.data?.message || e.message || "Unable to load client context.");
      } finally { setContextLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (contextLoading || error) return;
    (async () => {
      setLoading(true); setError("");
      try {
        const params = { mode: "detailed", pan: isFamily ? selectedPan : undefined };
        const displayPromise = axios.get(`${API_BASE}/get_client_sip.php`, { ...authConfig(), params: { ...params, status: sipType } });
        const activePromise = sipType === "active" ? displayPromise : axios.get(`${API_BASE}/get_client_sip.php`, { ...authConfig(), params: { ...params, status: "active" } });
        const [displayRes, activeRes] = await Promise.all([displayPromise, activePromise]);
        if (displayRes.data?.status !== "ok") throw new Error(displayRes.data?.message || "Unable to load SIPs.");
        if (activeRes.data?.status !== "ok") throw new Error(activeRes.data?.message || "Unable to load active SIP total.");
        setRows(Array.isArray(displayRes.data?.data) ? displayRes.data.data : []);
        setActiveRows(Array.isArray(activeRes.data?.data) ? activeRes.data.data : []);
      } catch (e) {
        if (handleClientAuthFailure(e.response, e.response?.data)) return;
        setRows([]); setActiveRows([]);
        setError(e.response?.data?.message || e.message || "Unable to load SIPs.");
      } finally { setLoading(false); }
    })();
  }, [contextLoading, isFamily, selectedPan, sipType]);

  const panOptions = useMemo(() => [
    { value: "ALL", label: "ALL" },
    ...members.map((m) => ({ value: m.pan, label: `${m.invester_name || m.child_name || "Client"} - ${m.pan}` })),
  ], [members]);

  const memberMap = useMemo(() => {
    const map = {};
    members.forEach((m) => { if (m.pan) map[m.pan] = m.invester_name || m.child_name || m.pan; });
    return map;
  }, [members]);

  const activeTotals = useMemo(() => {
    const byPan = {};
    let all = 0;
    activeRows.forEach((r) => {
      const v = monthlyActiveValue(r);
      all += v;
      byPan[r.pan] = (byPan[r.pan] || 0) + v;
    });
    return { all, byPan };
  }, [activeRows]);

  const groups = useMemo(() => {
    if (!(isFamily && selectedPan === "ALL")) return [{ pan: selectedPan, rows }];
    const grouped = {};
    rows.forEach((r) => { (grouped[r.pan] ||= []).push(r); });
    return Object.entries(grouped).map(([pan, data]) => ({ pan, rows: data }));
  }, [rows, isFamily, selectedPan]);

  const columns = useMemo(() => {
    const cols = [
      { title: "Folio", dataIndex: "folio_number", width: "18%" },
      { title: "Scheme", dataIndex: "scheme_name", width: "38%", ellipsis: false, onCell: () => ({ className: "kg-mobile-full-name-cell" }), render: (v) => <span className="kg-mobile-full-name" title={v}>{v || "-"}</span> },
      { title: "SIP Amount", dataIndex: "sip_amount", width: "16%", align: "right", render: (v) => `₹${money(v)}` },
      { title: "Frequency", dataIndex: "frequency", width: "14%" },
      { title: "SIP Day", dataIndex: "installment_day", width: "14%", render: (v) => v || "-" },
    ];
    if (sipType === "all") cols.push({ title: "Status", dataIndex: "sip_status", width: "12%", render: (v) => v === "RUNNING" ? "Active" : "Ceased" });
    return cols;
  }, [sipType]);

  if (contextLoading) return <div style={{ padding: 30, textAlign: "center" }}><Spin /></div>;

  return (
    <div className="kg-sips-page">
      <style>{`
        .kg-sips-page { width:100%; padding:4px 2px 24px; background:transparent; }
        .kg-sips-controls { display:flex; gap:14px; flex-wrap:wrap; align-items:flex-end; margin-bottom:14px; }
        .kg-sips-table .ant-table { background:transparent; font-size:${Math.max(10, 12 * zoom / 100)}px; }
        .kg-sips-table .ant-table-container, .kg-sips-table .ant-table-content { overflow-x:visible !important; }
        .kg-sips-table table { width:100% !important; table-layout:fixed !important; border-collapse:collapse !important; border:1px solid #000 !important; }
        .kg-sips-table th, .kg-sips-table td { white-space:nowrap !important; padding:${Math.max(4, 7 * zoom / 100)}px ${Math.max(4, 7 * zoom / 100)}px !important; overflow:hidden; text-overflow:ellipsis; border:1px solid #000 !important; }
        .kg-sips-table .ant-table-thead > tr > th { background:transparent !important; box-shadow:none !important; border-radius:0 !important; border-top:1px solid #000 !important; border-bottom:1px solid #000 !important; }
        .kg-sips-table .ant-table-thead > tr > th::before { display:none !important; }
        .kg-sips-section { margin-top:18px; }
        .kg-sips-section-head { display:flex; justify-content:space-between; gap:10px; align-items:baseline; border-bottom:1px solid #e8e8e8; padding:0 2px 6px; margin-bottom:4px; white-space:nowrap; }
        .kg-sips-total { font-weight:700; white-space:nowrap; }
        @media (max-width: 700px) {
          .kg-sips-page { width:100%; max-width:100%; padding:0 0 18px; overflow-x:hidden; }
          .kg-sips-page .ant-typography { font-size:20px !important; line-height:1.2 !important; margin-bottom:10px !important; }
          .kg-sips-controls { width:100%; gap:6px; align-items:stretch; }
          .kg-sips-control { min-width:0 !important; flex:1 1 100% !important; width:100%; }
          .kg-sips-controls > div:last-child { margin-left:0 !important; width:100%; text-align:right; }
          .kg-sips-total { white-space:normal; overflow-wrap:anywhere; font-size:11px; line-height:1.3; }
          .kg-sips-section { width:100%; max-width:100%; overflow:hidden; }
          .kg-sips-section-head { white-space:normal; flex-wrap:wrap; align-items:flex-start; font-size:10px; line-height:1.25; gap:3px 6px; }
          .kg-sips-table, .kg-sips-table .ant-spin-nested-loading, .kg-sips-table .ant-spin-container,
          .kg-sips-table .ant-table, .kg-sips-table .ant-table-container, .kg-sips-table .ant-table-content { width:100% !important; max-width:100% !important; overflow:hidden !important; }
          .kg-sips-table .ant-table { font-size:${Math.max(6, 8 * zoom / 100)}px; }
          .kg-sips-table table { width:100% !important; min-width:0 !important; table-layout:fixed !important; }
          .kg-sips-table th, .kg-sips-table td { min-width:0 !important; max-width:0; padding:2px 1px !important; line-height:1.15 !important; }
          .kg-sips-table th { white-space:normal !important; overflow-wrap:anywhere; word-break:break-word; text-align:center; }
          .kg-sips-table td { white-space:nowrap !important; }
          .kg-sips-table .ant-table-cell { box-sizing:border-box; }
        }
        @media (max-width: 420px) {
          .kg-sips-table .ant-table { font-size:${Math.max(6, 7 * zoom / 100)}px; }
          .kg-sips-table th, .kg-sips-table td { padding:2px 1px !important; }
        }
        @media (max-width: 700px) {
          .kg-mobile-full-name,
          .kg-mobile-full-name > span,
          .kg-mobile-full-name-cell,
          .kg-mobile-full-name-cell > span {
            white-space: normal !important;
            overflow: visible !important;
            text-overflow: clip !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            line-height: 1.15 !important;
            height: auto !important;
          }
          .kg-mobile-full-name-cell {
            font-size: 7px !important;
          }
        }
      `}
</style>

      <Typography.Title level={2} style={{ marginTop: 0, marginBottom: 12 }}>My SIPs</Typography.Title>
      <div className="kg-sips-controls">
        {isFamily && <div className="kg-sips-control" style={{ minWidth: 245 }}><div style={{ fontWeight: 600, marginBottom: 5 }}>SIP of</div><Select value={selectedPan} onChange={setSelectedPan} options={panOptions} style={{ width: "100%" }} /></div>}
        <div className="kg-sips-control" style={{ minWidth: 175 }}><div style={{ fontWeight: 600, marginBottom: 5 }}>SIP Type</div><Select value={sipType} onChange={setSipType} style={{ width: "100%" }} options={[{ value:"all", label:"All" }, { value:"active", label:"Active" }, { value:"ceased", label:"Ceased" }]} /></div>
        <div style={{ marginLeft: "auto", whiteSpace: "nowrap" }}><Button size="small" onClick={() => setZoom((z) => Math.max(75, z - 10))}>−</Button><span style={{ display:"inline-block", minWidth:48, textAlign:"center", fontSize:12 }}>{zoom}%</span><Button size="small" onClick={() => setZoom((z) => Math.min(130, z + 10))}>+</Button></div>
      </div>

      <div className="kg-sips-total" style={{ marginBottom: 10 }}>Monthly Active SIP Total: ₹{money(activeTotals.all)}</div>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

      <Spin spinning={loading}>
        {!loading && rows.length === 0 ? (
          <div style={{ fontWeight: 700, padding: "12px 2px" }}>No SIP Found</div>
        ) : (
          groups.map((g) => (
            <div className="kg-sips-section" key={g.pan || "single"}>
              {isFamily && selectedPan === "ALL" && <div className="kg-sips-section-head"><strong className="kg-mobile-full-name">{memberMap[g.pan] || g.pan}</strong><span className="kg-sips-total">Monthly Active SIP Total: ₹{money(activeTotals.byPan[g.pan] || 0)}</span></div>}
              <Table className="kg-sips-table" rowKey={(r) => r.id || `${r.pan}-${r.folio_number}-${r.scheme_code}-${r.sip_amount}-${r.frequency}`} columns={columns} dataSource={g.rows} loading={false} pagination={false} size="small" />
            </div>
          ))
        )}
      </Spin>
    </div>
  );
}
